import range from 'lodash/range'

import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import LinearProgress from '@components/LinearProgress'
import SearchInput from '@components/SearchInput'
import TableFilters from '@components/TableFilters'

import SOP from './SOP'
import { areFiltersApplied, filtersResetState, useFiltersSchema, useSOPs, useSOPsFilters } from './SOPsList.hooks'

export default function SOPsList() {
  const [filters, updateFilters] = useSOPsFilters()
  const schema = useFiltersSchema()

  const { data, isPending, isRefreshing, fetchNextPage, hasNextPage, isFetchingNextPage } = useSOPs()

  const hasSearch = areFiltersApplied(filters)
  const isEmpty = !isPending && !hasSearch && data.length === 0
  const isEmptySearch = !isPending && hasSearch && data.length === 0
  const hasData = !isPending && !isEmpty && !isEmptySearch

  return (
    <Stack>
      <LinearProgress loading={isRefreshing} />

      <Stack sx={styles.filters}>
        <SearchInput
          searchOnEnter
          size="small"
          placeholder="Search on 'Enter'"
          value={filters.sopsSearch ?? ''}
          onChange={(value) => updateFilters({ sopsSearch: value || undefined })}
          data-testid="sops-search"
        />
        <TableFilters
          schema={schema}
          values={filters}
          onChange={updateFilters}
          showReset={areFiltersApplied(filters)}
          onReset={() => updateFilters(filtersResetState)}
        />
      </Stack>

      <Stack sx={{ gap: 2 }}>
        {isPending && (
          <Stack sx={styles.rows}>
            {range(5).map((i) => (
              <SOP.Loading key={i} />
            ))}
          </Stack>
        )}
        {isEmpty && <Empty data-testid="sops-empty">No SOPs have been added yet</Empty>}
        {isEmptySearch && <Empty data-testid="sops-not-found">We could not find a SOP that matches, please try a different search</Empty>}
        {hasData && (
          <Stack sx={styles.rows}>
            {data.map((sop) => (
              <SOP
                key={sop.id}
                data={sop}
                selected={String(sop.id) === String(filters.sopId)}
                onSelect={() => updateFilters({ sops: 'view', sopId: sop.id })}
              />
            ))}
          </Stack>
        )}
        {hasNextPage && (
          <Button
            fullWidth
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            endIcon={<MoreOutlinedIcon rotate={90} />}
            loadingPosition="end"
            sx={{ textTransform: 'none' }}
            data-testid="sops-load-more"
          >
            more
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

function Empty({ children, ...rest }) {
  return (
    <Stack sx={{ p: 2, alignItems: 'center', justifyContent: 'center' }} {...rest}>
      <Typography variant="h5" sx={{ textAlign: 'center', color: 'text.secondary' }}>
        {children}
      </Typography>
    </Stack>
  )
}

const styles = {
  filters: {
    p: 1,
    gap: 1,
  },
  rows: {
    px: 1,
    gap: 0.25,
  },
}
